/* tslint:disable:max-line-length */
/**
 * Created by ppatton on 12/13/2016.
 */

import { Injectable } from '@angular/core';
import { AppConstants } from '@utils/app.constants';
import { LMActionService } from '@services/lm-action.service';
import { LMCommonUtilService } from '@utils/lm-common-util.service';
import { LMLocaleUtilService } from '@utils/lm-locale-util.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMEditorService } from '@list/editors/lm-editor.service';
import { LMFormatterService } from '@list/formatters/lm-formatter.service';
import { LMFieldUtilService } from '@services/utils/lm-field-util.service';
import { LMConditionUtilService } from '@utils/lm-condition-util.service';
import { LMListUtilService } from '@list/services/lm-list-util.service';

import { isCheckControlEditable } from '@list/editors/lm-checkcontrol-editable.editor';
import { isEditable } from '@list/editors/lm-is-editable.editor';
import { lmBcdSerializer } from '@list/editors/lm-bcd.serializer';
import { lmBooleanSerializer } from '@list/editors/lm-boolean.serializer';
import { lmDateTimeSerializer } from '@list/editors/lm-date-time.serializer';
import { lmBarCodeClickFunction } from '@list/editors/lm-barcode-click.function';
import { lmLookupClickFunction } from '@list/editors/lm-lookup-click.function';
import { lmCodeBlockSerializer } from '@list/editors/lm-code-block.serializer';
import { lmPercentSerializer } from '@list/editors/lm-percent.serializer';
import { lmStateFieldSource } from '@list/editors/lm-state-field.source';
import { lmButtonFormatter } from '@list/formatters/lm-button.formatter';
import { lmCheckBoxFormatter } from '@list/formatters/lm-checkbox.formatter';
import { lmCodeBlockFormatter } from '@list/formatters/lm-code-block.formatter';
import { lmFavoriteFormatter } from '@list/formatters/lm-favorite.formatter';
import { lmProgressFormatter } from '@list/formatters/lm-progress.formatter';
import { lmTreeFormatter } from '@list/formatters/lm-tree.formatter';
import { lmCardViewFormatter } from '@list/formatters/lm-card-view.formatter';
import { LMListSpecUtilService } from './lm-list-spec-util.service';
import { LMListCardService } from './lm-list-card.service';
import { LMCompoundFieldService } from '@services/lm-compound-field.service';
import { colSpan } from '@list/formatters/lm-colspan.formatter';
import { lmListCellTooltip } from '@list/formatters/lm-list-cell-tooltip.formatter';
import { lmFieldFormatter } from '@list/formatters/lm-field.formatter';
import { LMCodeBlockEditorComponent } from '@list/editors/lm-code-block.editor';
import { LMUrlUtilService } from '@services/utils';
import { LMContextService, LMUrlInfoBuilderService } from '@services/index';
import { lmMaskOptionsFunction } from '@list/editors/lm-maskoptions.function';
import { lmFieldLookupClickFunction } from '@list/editors/lm-fieldlookup-click.function';
import { lmFilterLookupClickFunction } from '@list/editors/lm-filter-lookup-click.function';
import { lmFilterClearClickFunction } from '@list/editors/lm-filter-clear-click.function';
import { lmFilterDropdownKeydownFunction } from '@list/editors/lm-filter-dropdown-keydown.function';
import { LMGlobalPersonalizationService } from '@states/services';
import { lmRatingFormatter } from '@list/formatters/lm-rating.formatter';
import { lmProcessIndicatorFormatter } from '../formatters/lm-process-indicator.formatter';
import { LMProcessIndicatorService } from '../../field/processindicator/lm-process-indicator.service';
import { LMProcessIndicatorTooltipPipe } from '@field/processindicator/lm-process-indicator-tooltip.pipe';

@Injectable()
export class LMListGridBuilderService {

  private alphaFilterConditions: Array<any> = [ 'contains', 'does-not-contain', 'equals',
    'does-not-equal', 'is-empty', 'is-not-empty', 'end-with', 'does-not-end-with', 'start-with', 'does-not-start-with' ];

  private numericFilterConditions: Array<SohoDataGridColumnFilterConditions> = [ 'equals', 'does-not-equal', 'is-empty', 'is-not-empty', 'less-than', 'less-equals', 'greater-than', 'greater-equals' ];
  private codeBlockFilterConditions: Array<SohoDataGridColumnFilterConditions> = [ 'contains' ];
  private minColWidth = 100;

  constructor(
    private lmActionService: LMActionService,
    private lmCommonUtilService: LMCommonUtilService,
    private lmContextService: LMContextService,
    private lmEditorService: LMEditorService,
    private lmFormatterService: LMFormatterService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmListSpecUtilService: LMListSpecUtilService,
    private lmLocaleUtilService: LMLocaleUtilService,
    private lmFieldUtilService: LMFieldUtilService,
    private lmConditionUtilService: LMConditionUtilService,
    private lmListCardService: LMListCardService,
    private lmCompoundFieldService: LMCompoundFieldService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmProcessIndicatorService: LMProcessIndicatorService,
    private lmProcessIndicatorTooltipPipe: LMProcessIndicatorTooltipPipe,
    private lmListUtilService: LMListUtilService
  ) { }

  public updateDataGridColumn(dataGridColumns: SohoDataGridColumn[], showSelectionColumn: boolean) {
    if (showSelectionColumn) {
      if (dataGridColumns.length === 0 || dataGridColumns[0].id !== 'selectionCheckbox') {
        const checkBoxColumn: ListGridColumn = {
          id:        'selectionCheckbox',
          sortable:  false,
          resizable: false,
          formatter: this.LMCheckBoxFormatter,
          align:     'center'
        };

        dataGridColumns.unshift(checkBoxColumn);
      }
    } else if (dataGridColumns.length > 0 && dataGridColumns[0].id === 'selectionCheckbox') {
      dataGridColumns.shift();
    }
  }

  public buildDataGridOptions(
    listUrlInfo: LMListUrlInfo,
    listSpec: LMListSpec,
    listState: LMListState,
    listPersonalization: LMListPersonalization,
    allowMultiSelections: boolean
  ): SohoDataGridOptions {
    const dataGridColumns: SohoDataGridColumn[] = [];

    const showSelectionColumn: boolean = this.showSelectionColumn(listSpec, undefined, allowMultiSelections);
    this.updateDataGridColumn(dataGridColumns, showSelectionColumn);

    // -----------------------------------------------------------------
    // build the datagrid columns
    // -----------------------------------------------------------------

    listSpec.busListSpec.displayFields[ 0 ].isTreeColumn = listSpec.busListSpec.implementsTreeView;

    const detailFieldLength = listSpec?.busListSpec?.detailFields?.length;
    let columns: LMListDisplayField[] = listSpec?.busListSpec?.displayFields;
    // make the column hidden, this will be the value storage for the detail field when modified
    if (detailFieldLength) {
      const detailFields = [ ...listSpec.busListSpec.detailFields ];
      detailFields.forEach(detailField => { detailField.columnVisibility = [ { type: 'value', value: false } ]; });
      columns = [ ...columns, ...detailFields ];
    }
    dataGridColumns.push(...columns.map((displayField, index) =>
      this.buildColumn(listUrlInfo, listSpec, displayField, index, listPersonalization)));

    const showDirty = listSpec.busListSpec.implementsInlineCreate || this.lmListSpecUtilService.allowUpdate(listSpec);
    const editable = listSpec.listType !== 'select' && !listSpec.showAsMingleWidget;

    const useCompactMode = this.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout;
    const rowHeight = useCompactMode ? 'extra-small' : 'small';
    const columnSizing = (useCompactMode && !listSpec.busListSpec.horizontalScrolling) ? 'data' : 'both';
    const cellNavigation = !(listSpec.listType === 'select' && this.lmContextService.useSingleSelectList());

    const gridOptions: SohoDataGridOptions = {
      cellNavigation,
      columns:              dataGridColumns,
      disableClientSort:    true,
      disableClientFilter:  true,
      filterWhenTyping:     false, // !this.lmListUtilService.isAutoFilterEnabled(),
      redrawOnResize:       false, // todo ppatton remove once tim fixes all the resize issues
      menuId:               this.lmListSpecUtilService.getContextMenuId(listSpec),
      isList:               listSpec.busListSpec.showAsSimpleList || listSpec.showAsMingleWidget,
      editable,
      enableTooltips:       true,
      rowHeight,
      columnSizing,
      // todo ppatton - need to store columns meta ourselves in listPersonalization
      saveUserSettings:     {
        columns: false,
        rowHeight: false,
        sortOrder: false,
        pagesize: false,
        activePage: false,
        filter: false
      },
      toolbar: null,
      actionableMode: true,
      showDirty,
      emptyMessage: null, // reset the default empty message to be null so it doesn't display on initial load of recs
      // Disable total rows, prevent them from being selected
      isRowDisabled: (rowIdx, dataView) => !!dataView.subtotalLabel,

      // this allows us a more reliable id but it must be done via testversion 3.
      uniqueId: this.lmContextService.testVersion() >= AppConstants.TESTVERSION_V3 ? listSpec.busListSpec.id : undefined
    };

    const columnGroups: SohoDataGridColumnGroup[] = this.buildColumnGroups(listSpec, showSelectionColumn);
    if (columnGroups && columnGroups.length > 0) {
      gridOptions.columnGroups = columnGroups;
    }

    this.setFilterable(gridOptions, listSpec, listState);
    this.setSelectionMode(gridOptions, listSpec, showSelectionColumn, allowMultiSelections);
    this.setFreezeColumns(gridOptions, listSpec);

    return gridOptions;
  }

  public buildAggregationDataGridOptions(
    listUrlInfo: LMListUrlInfo,
    listSpec: LMListSpec,
    listState: LMListState,
    listPersonalization: LMListPersonalization,
    chartName: string
  ): SohoDataGridOptions {
    const allowMultiSelections = false;
    const dataGridColumns: SohoDataGridColumn[] = [];

    // -----------------------------------------------------------------
    // build the datagrid columns
    // -----------------------------------------------------------------

    dataGridColumns.push(...listSpec.busListSpec.charts[chartName].aggFields.map((aggField, index) =>
      this.buildAggColumn(listUrlInfo, listSpec, aggField, index)));

    const useCompactMode = this.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout;
    const rowHeight = useCompactMode ? 'extra-small' : 'small';
    const columnSizing = (useCompactMode && !listSpec.busListSpec.horizontalScrolling) ? 'data' : 'both';

    const gridOptions: SohoDataGridOptions = {
      cellNavigation:       true,
      columns:              dataGridColumns,
      disableClientSort:    true,
      disableClientFilter:  true,
      filterWhenTyping:     false, //!this.lmListUtilService.isAutoFilterEnabled(),
      redrawOnResize:       false, // todo ppatton remove once tim fixes all the resize issues
      menuId:               this.lmListSpecUtilService.getContextMenuId(listSpec) + '_agg',
      isList:               true,
      editable:             false,
      enableTooltips:       true,
      rowHeight,
      columnSizing,
      // todo ppatton - need to store columns meta ourselves in listPersonalization
      saveUserSettings:     {
        columns: false,
        rowHeight: false,
        sortOrder: false,
        pagesize: false,
        activePage: false,
        filter: false
      },
      toolbar: null,
      actionableMode: true,
      showDirty: false,
      emptyMessage: null, // reset the default empty message to be null so it doesn't display on initial load of recs
      // Disable total rows, prevent them from being selected
      isRowDisabled: (rowIdx, dataView) => !!dataView.subtotalLabel
    };

    // LMWEB-1644 Both column groups and freeze columns are set based on the main list columns and not on agg chart fields.
    // Commenting it out until there is an enhancement request to make groups and freeze columns available on the agg table itself.
    // const columnGroups: SohoDataGridColumnGroup[] = this.buildColumnGroups(listSpec, false);
    // if (columnGroups && columnGroups.length > 0) {
    //   gridOptions.columnGroups = columnGroups;
    // }

    this.setFilterable(gridOptions, listSpec, listState);
    this.setSelectionMode(gridOptions, listSpec, false, allowMultiSelections);
    // LMWEB-1644 Both column groups and freeze columns are set based on the main list columns and not on agg chart fields.
    // Commenting it out until there is an enhancement request to make groups and freeze columns available on the agg table itself.
    // this.setFreezeColumns(gridOptions, listSpec);

    return gridOptions;
  }

  public buildColumnsAfterPeriodOverride(
    listUrlInfo: LMListUrlInfo,
    listSpec: LMListSpec,
    listState: LMListState,
    listPersonalization: LMListPersonalization,
  ): ListGridColumn[] {
    const dataGridColumns: ListGridColumn[] = [];
    listSpec.busListSpec.displayFields[ 0 ].isTreeColumn = listSpec.busListSpec.implementsTreeView;
    dataGridColumns.push(...listSpec.busListSpec.displayFields.map((displayField, index) =>
      this.buildColumn(listUrlInfo, listSpec, displayField, index, listPersonalization)));
    return dataGridColumns;
  }

  private showSelectionColumn(listSpec: LMListSpec, inSelectListEditMode?: boolean, allowMultiSelections?: boolean): boolean {
    const selectionMethod = this.lmListSpecUtilService.getSelectionMode(listSpec, inSelectListEditMode, allowMultiSelections);

    return selectionMethod === 'mixed' || selectionMethod === 'multiple';
  }

  public buildColumnGroups(listSpec: LMListSpec, hasSelectionColumn: boolean): SohoDataGridColumnGroup[] {
    const columnGroups: SohoDataGridColumnGroup[] = [];

    // -------------------------------------------------------------------------------
    // if list is selectable, push the grouped headers over one column (not to
    // include checkbox column)
    // -------------------------------------------------------------------------------
    if (hasSelectionColumn) {
      // list is selectable, so push the grouped headers over one column (not to include checkbox column)
      columnGroups.push({ colspan: 1, id: '', name: '' });
    }

    let hasAtLeastOneGroup = false;
    for (let i = 0, len = listSpec.busListSpec.displayFields.length; i < len; i++) {
      const displayField: LMListDisplayField = listSpec.busListSpec.displayFields[ i ];

      // -------------------------------------------------------------------------------
      // if no group label id but there are columnGroups then add a blank column
      // -------------------------------------------------------------------------------
      if (!displayField.groupLabelId) {
        // no group label id, so have a blank column
        columnGroups.push({ colspan: 1, id: '', name: '' });
        continue;
      }

      // -------------------------------------------------------------------------------
      // No column groups exist at all, add a new one
      // -------------------------------------------------------------------------------
      if (columnGroups.length === 0) {
        // No column groups exist at all, add a new one
        columnGroups.push({ colspan: 1, id: displayField.groupLabelId, name: displayField.groupLabel.resourceValue, align: 'center' });
        hasAtLeastOneGroup = true;
        continue;
      }

      // -------------------------------------------------------------------------------
      // Check if the groupId is already in the column groups
      // -------------------------------------------------------------------------------
      let groupLabelExists = false;

      if (columnGroups.length > 0 && columnGroups[columnGroups.length - 1].id === displayField.groupLabelId) {
        groupLabelExists = true;

        // -------------------------------------------------------------------------------
        // it exists, increment how large the column span should be
        // -------------------------------------------------------------------------------
        const columnsInGroup = columnGroups[columnGroups.length - 1].colspan;
        columnGroups[columnGroups.length - 1].colspan = columnsInGroup + 1;
      }
      if (!groupLabelExists) {
        // -------------------------------------------------------------------------------
        // column group doesn't exist yet, add a new one
        // -------------------------------------------------------------------------------
        columnGroups.push({ colspan: 1, id: displayField.groupLabelId, name: displayField.groupLabel.resourceValue, align: 'center' });
        hasAtLeastOneGroup = true;
      }
    }

    if (hasAtLeastOneGroup) {
      const detailFieldLength = listSpec?.busListSpec?.detailFields?.length;
      if (detailFieldLength) {
        for (let i = 0; i < detailFieldLength; i++) {
          columnGroups.push({ colspan: 1, id: '', name: '' });
        }
      }
      return columnGroups;
    } else {
      return null;
    }
  }

  private setFilterable(gridOptions: SohoDataGridOptions, listSpec: LMListSpec, listState: LMListState) {
    if (listSpec.busListSpec.searchForm) {
      // ---------------------------------------------------------------------------
      // If there is a searchForm then it's the responsibility of lm-list.component
      // to restore it's state. Set filterable to false so the filter bar doesn't
      // display.
      // ---------------------------------------------------------------------------
      gridOptions.filterable = false;
    } else {
      switch (listSpec.busListSpec.displaySearch) {
        case 'initially':
          if (listState.isFilterBarOpened !== undefined && !listState.isFilterBarOpened) {
            gridOptions.filterable = false;
          } else {
            gridOptions.filterable = true;
            listState.isFilterBarOpened = true;
          }
          break;

        case 'always':
          gridOptions.filterable = true;
          listState.isFilterBarOpened = true;
          break;

        case 'default':
          if (listSpec.listType === 'select') {
            gridOptions.filterable = true;
            listState.isFilterBarOpened = true;
          } else {
            gridOptions.filterable = listState.isFilterBarOpened;
          }
          break;
      }
    }
  }

  private setSelectionMode(gridOptions: SohoDataGridOptions, listSpec: LMListSpec, showSelectionColumn: boolean, allowMultiSelections?: boolean): void {
    const selectionMode: SohoDataGridSelectable = this.lmListSpecUtilService.getSelectionMode(listSpec, undefined, allowMultiSelections);

    if (selectionMode === false) {
      if (!listSpec.busListSpec.openActionRestricted) {
        /**
         * turn off cellNavigation so we get the hand cursor when
         * mousing over a list row. and a single click is fired
         * when clicking on a row.
         */
        gridOptions.cellNavigation = false;
        gridOptions.selectable = false;
      }

      return;
    }

    switch (selectionMode) {
      case 'single':
        if (showSelectionColumn) {
          gridOptions.selectable = 'multiple';
        } else {
          gridOptions.selectable = selectionMode;
        }
        break;
      case 'multiple':
      case 'siblings':
        gridOptions.selectable = selectionMode;
        break;

      case 'mixed':
        gridOptions.selectable = selectionMode;
        gridOptions.disableRowDeactivation = true;
        break;
    }
  }

  private setFreezeColumns(gridOptions: SohoDataGridOptions, listSpec: LMListSpec): void {
    if (listSpec.busListSpec.implementsCubeView) {
      return;
    }

    if (listSpec.busListSpec.horizontalScrolling && listSpec.busListSpec.freezeColumns) {

      // init frozen columns structure
      gridOptions.frozenColumns = { left: [] };

      // find the correct number of freeze column
      const freezeColumns = listSpec.busListSpec.freezeColumns + (this.showSelectionColumn(listSpec) ? 1 : 0);

      // set which columns are to be frozen
      for (let i = 0; i < freezeColumns; i++) {
        const gridColumn: SohoDataGridColumn = gridOptions.columns[i];
        gridOptions.frozenColumns.left.push(gridColumn.id);

        // Handle frozen columns that are not scrollable
        if (gridColumn.colspan) {
          gridColumn.resizable = false;
        } else if (gridColumn.hasOwnProperty('displayField')) {
          const displayField = gridColumn['displayField'] as LMListDisplayField;
          if (displayField.type === 'CARDVIEW') {
            gridColumn.resizable = false;
          }
        }
      }

      if (this.hasCardView(listSpec.busListSpec.displayFields) || this.hasCodeBlock(listSpec.busListSpec.displayFields)) {
        gridOptions.fixedRowHeight = 'auto';
      }
    }
  }

  public updateFrozenColumns(frozenColumns: SohoDataGridFrozenColumns, showSelectionColumn: boolean): void {
    if (frozenColumns) {
      const leftFrozenColumns = frozenColumns.left;
      if (showSelectionColumn) {
        if (leftFrozenColumns.length > 0 && leftFrozenColumns[0].id !== 'selectionCheckbox' ) {
          leftFrozenColumns.unshift('selectionCheckbox');
        }
      } else if (leftFrozenColumns.length > 0 && leftFrozenColumns[0].id === 'selectionCheckbox') {
        leftFrozenColumns.shift();
      }
    }
  }

  public updateColumnGroups(columnGroups: SohoDataGridColumnGroup[], showSelectionColumn: boolean): void {
    if (columnGroups) {
      if (showSelectionColumn) {
        if (columnGroups.length > 0 && columnGroups[0].id !== 'selectionCheckbox' ) {
          columnGroups.unshift({ colspan: 1, id: 'selectionCheckbox', name: '' });
        }
      } else if (columnGroups.length > 0 && columnGroups[0].id === 'selectionCheckbox') {
        columnGroups.shift();
      }
    }
  }

  private buildColumn(listUrlInfo: LMListUrlInfo, listSpec: LMListSpec, displayField: LMListDisplayField, columnNumber: number, personalization: LMListPersonalization): ListGridColumn {
    let bindId: string;
    const type: string = displayField.type.toUpperCase();

    // ----------------------------------------------------------------------------
    // datagrid doesn't handle a cell with a fieldId not being tied to a value
    // in the dataset. Buttons and FormText don't have data in the dataset. So
    // set fieldId to undefined in these cases so datagrid doesn't blow up trying
    // trying to find said value.
    // ----------------------------------------------------------------------------
    if (type !== AppConstants.FORMBUTTON && type !== AppConstants.NAVIGATION && type !== AppConstants.FORMREFERENCE && type !== AppConstants.LISTREFERENCE && type !== AppConstants.FORMTEXT) {
      bindId = 'fields.' + displayField.fieldId + '.value';

      // template list display values are in the target dataView
      // not the template dataView.  The template dataView is needed for paging or positioning
      if (listSpec.isTemplateList) {
        bindId = 'dataView.' + bindId;
      }
    }

    const isRequiredSearchParameter = this.lmListSpecUtilService.isRequiredSearchParameter(listSpec.busListSpec, displayField.fieldId);

    // a column could appear on a list multiple times (under different labels), so uniquify the id for the column

    let columnId = displayField.columnId;
    if (columnId && this.lmContextService.testVersion() <= 2) {
      columnId = displayField.columnId + '_' + columnNumber;
    }

    const column: ListGridColumn = {
      id:         columnId,
      field:      bindId,
      sortable:   this.isSortable(listSpec, displayField),
      fieldId:    displayField.fieldId,
      // adding to column object so we can have access to this in the formatter and editors
      displayField,
      isRequiredSearch: isRequiredSearchParameter,
      minWidth: this.minColWidth
    };

    let columnName = '';
    if (displayField.labelSuppressed) {
      columnName = '';
    } else if (displayField.label && displayField.label.resourceValue && !displayField.label.variables) {
      columnName = displayField.label.resourceValue;
    } else if (displayField.title && displayField.title.resourceValue && !displayField.title.variables) {
      columnName = displayField.title.resourceValue;
    }

    if (isRequiredSearchParameter && !listSpec.busListSpec.searchForm) {
      columnName = columnName + '*';
    }
    column.name = columnName;

    if (!!displayField.showChars) {
      let colNumber = columnNumber;
      if (listSpec.busListSpec.selection !== 'disabled') {
        // the 0th column is the checkbox selection
        colNumber = colNumber + 1;
      }
      if (!(personalization.columns && personalization.columns[colNumber] && personalization.columns[colNumber].width)) {
        this.setColumnWidthWithShowUpTo(column, displayField, listSpec);
      }
    }
    this.setFormatInfo(listSpec, column, displayField);
    this.setFilterInfo(listUrlInfo, listSpec, column, displayField);
    this.setColumnEditor(listUrlInfo, listSpec, column, displayField);
    this.setColumnAlignment(column, displayField);
    this.setColumnSpan(listSpec, column);

    return column;
  }

  private buildAggColumn(listUrlInfo: LMListUrlInfo, listSpec: LMListSpec, aggField: LMAggField, columnNumber: number): ListGridColumn {
    const bindId: string = 'fields.' + aggField.fieldId + '.value';

    const displayField: LMListDisplayField = {
      fieldId: aggField.fieldId,
      columnId: aggField.columnId,
      title: aggField.label,
      decSize: aggField.decSize,
      isTotal: aggField.isTotal,
      label: aggField.label,
      positiveOnly: aggField.positiveOnly,
      states: aggField.states,
      type: aggField.type,
      size: aggField.size,
      isKey: aggField.isKey,
      isStaticStateField: aggField.isStaticStateField,
      isUserDefinedStateField: aggField.isUserDefinedStateField,
      displayBlankWhenZero: aggField.displayBlankWhenZero,
      numberOfDecimals: aggField.numberOfDecimals,
      hasCurrencySymbol: aggField.hasCurrencySymbol,
      currencySymbolInfo: aggField.currencySymbolInfo,
      displayNegativeAmountAs: aggField.displayNegativeAmountAs,
      showPercentSymbol: aggField.showPercentSymbol,
      percentSymbolFirstLineAndTotalsOnly: aggField.percentSymbolFirstLineAndTotalsOnly,
      linkNavigation: aggField.linkNavigation,
      showAsPdf: aggField.showAsPdf,
      resourceId: aggField.resourceId,
      aggDrill: aggField.aggDrill,
      caption: aggField.caption,
    };

    const isRequiredSearchParameter = this.lmListSpecUtilService.isRequiredSearchParameter(listSpec.busListSpec, aggField.fieldId);
    const column: ListGridColumn = {
      id:         aggField.columnId + '_' + columnNumber,
      field:      bindId,
      sortable:   columnNumber === 0,
      fieldId:    aggField.fieldId,
      // adding to column object so we can have access to this in the formatter and editors
      displayField,
      isRequiredSearch: false,
      isAggField: true,
      minWidth: this.minColWidth
    };

    let columnName = '';
    if (aggField.label && aggField.label.resourceValue && !aggField.label.variables) {
      columnName = aggField.label.resourceValue;
    }

    if (isRequiredSearchParameter && !listSpec.busListSpec.searchForm) {
      columnName = columnName + '*';
    }
    column.name = columnName;

    this.setFormatInfo(listSpec, column, displayField);
    this.setFilterInfo(listUrlInfo, listSpec, column, displayField);
    this.setColumnEditor(listUrlInfo, listSpec, column, displayField);
    this.setColumnAlignment(column, displayField);
    this.setColumnSpan(listSpec, column);

    return column;
  }

  private setFormatInfo(listSpec: LMListSpec, column: ListGridColumn, displayField: LMListDisplayField) {

    let selectedFormatter: SohoDataGridColumnFormatterFunction;
    const formatterType = displayField.type = displayField.type.toUpperCase();
    column.textOverflow = 'ellipsis';

    // need to add the icon data on the column object for SOHO to consume
    if (displayField.icon) {
      column.icon = displayField.icon;
      column.iconFile = displayField.iconUrl;
    }

    if (this.isTextAreaOrText(displayField)) {
      column.width = 300;

      if (!this.hasCardView(listSpec.busListSpec.displayFields)) {
        // for multi-line text that is NOT displayed with a cardview, display it
        // as a single line and show a tooltip to display the entire contents
        column.singleline = true;
        column.contentTooltip = true;
      }
    }

    // Need this to capture the column click on a "more" link
    if (displayField.isTreeColumn) {
      column.isActionable = true;
    }

    if (displayField.isUserDefinedStateField || displayField.isStaticStateField) {
      if (displayField.linkNavigation || displayField.hasHttpUrl || displayField.cubeDrill || displayField.aggDrill) {
        column.isActionable = true;
      }
      selectedFormatter = this.LMFieldFormatter;
    } else if (displayField.displayAsProgressBar) {
      selectedFormatter = this.LMProgressFormatter;
      column.tooltip = this.LMListCellTooltip;
    } else if (displayField.isProcessIndicator) {
      selectedFormatter = this.LMProcessIndicatorFormatter;
      column.tooltip = this.LMListCellTooltip;
    } else if (displayField.compactFormatByForm) {
      selectedFormatter = this.LMCodeBlockFormatter;
      // column.expandOnActivate = true;
      column.flatLayout = this.lmCompoundFieldService.flattenLayout(displayField.layout);
      column.tooltip = this.LMListCellTooltip;
    } else if (displayField.displayAsRating) {
      selectedFormatter = this.LMRatingFormatter;
      column.tooltip = this.LMListCellTooltip;
      column.width = 200;
    } else {
      switch (formatterType) {
        case AppConstants.CARDVIEW:
          selectedFormatter = this.LMCardViewFormatter;
          column.isActionable = true;
          column.width = displayField.rightColumn ? 520 : 360;
          column.tooltip = this.LMListCellTooltip;
          column.cssClass = 'lm-datagrid-card-container';
          break;

        case AppConstants.CHECKCONTROL:
          if (displayField.displayAsStar) {
            column.showEmpty = true;
          }
          selectedFormatter = this.LMFavoriteFormatter;
          break;
        case AppConstants.FORMBUTTON:
        case AppConstants.FORMREFERENCE:
        case AppConstants.LISTREFERENCE:
        case AppConstants.NAVIGATION:
        case AppConstants.SUBFORM:
          column.isActionable = true;
          selectedFormatter = this.LMButtonFormatter;
          break;
        case AppConstants.TEXT:
          if (!displayField.isRichText) {
            column.tooltip = this.LMListCellTooltip;
          }
          // do not add break
        case AppConstants.TEXTAREA:
        case AppConstants.CURRENCY:
        case AppConstants.YYYY:
        case AppConstants.YYYYMM:
        case AppConstants.MMDD:
        case AppConstants.YYYYMMDD:
        case AppConstants.TIMESTAMP:
        case AppConstants.TIME:
        case AppConstants.NUMERIC:
        case AppConstants.BCD:
        case AppConstants.PERCENT:
        case AppConstants.BOOLEAN:
        case AppConstants.BINARY:
        case AppConstants.ALPHAUPPER:
        case AppConstants.ALPHALOWER:
        case AppConstants.ALPHARIGHT:
        case AppConstants.FORMTEXT:
          if (displayField.linkNavigation || displayField.hasHttpUrl || displayField.cubeDrill || displayField.aggDrill) {
            column.isActionable = true;
          }
          selectedFormatter = (displayField.type === AppConstants.TEXTAREA || displayField.type === AppConstants.TEXT) && displayField.isProcessIndicator
            ? this.LMProcessIndicatorFormatter
            : this.LMFieldFormatter;
          break;
        default:
          selectedFormatter = this.LMFieldFormatter;
          break;
      }
    }

    column.formatter = selectedFormatter;
  }

  private setFilterInfo(listUrlInfo: LMListUrlInfo, listSpec: LMListSpec, column: ListGridColumn, displayField: LMListDisplayField) {
    const type = displayField.type.toUpperCase();

    if (displayField.filterable) {
      if (displayField.isStaticStateField) {
        column.filterType = 'multiselect';
        if (displayField.states !== undefined) {
          column.filterRowEditorOptions = this.lmFormatUtilService.fieldStatesToOptionsArray(displayField.states, true);
          if (column.filterRowEditorOptions[0].label === '') {
            column.filterRowEditorOptions[0].label = Soho.Locale.translate('ListStaticFieldFilterNotEntered');
            column.filterRowEditorOptions[0].value = 'Not Entered';
          }
          column.editorOptions = {
            showSelectAll: true,
            //            onKeyDown: this.LMFilterDropdownKeydownFunction
          };
          if (!this.lmListUtilService.isAutoFilterEnabled()) {
            column.editorOptions.onKeyDown = this.LMFilterDropdownKeydownFunction;
          }
        }
        return;
      } else if (displayField.isUserDefinedStateField) {
        column.filterType = 'multiselect';
        column.filterRowEditorOptions = [ { label: ' ', value: ' ' } ];
        column.editorOptions = {
          showSelectAll: true,
          source:   this.LMStateFieldSource,
          //          onKeyDown: this.LMFilterDropdownKeydownFunction
        };
        if (!this.lmListUtilService.isAutoFilterEnabled()) {
          column.editorOptions.onKeyDown = this.LMFilterDropdownKeydownFunction;
        }
        return;
      }

      if (displayField.selectable && listSpec.listType !== 'select') {
        const selectListUrl = this.lmUrlUtilService.buildSelectListUrl(listUrlInfo, displayField.selectListInfo);
        if (!this.lmContextService.useNewSpecUrls()) {
          this.lmUrlInfoBuilderService.compareSpecUrls(listUrlInfo, displayField.bindId + '.selectListUrl', (displayField as any).selectListUrl, selectListUrl);
        }

        // todo spec-url-refactor, have to piggy back on displayfield for selectListUrl for some reason.
        //   can't just put selectListUrl into clickArguments, seems to get overridden.

        if (displayField.scannable && this.lmContextService.insideInforGo()) {
          displayField.id = column.id;
          displayField.selectListUrl = selectListUrl;
          column.filterType = 'lookup';
          column.editorOptions = {
            id: displayField.id,
            editable: true,
            icon: 'icon-barcode',
            click:    this.LMBarCodeClickFunction,
            clickArguments: {
              column: { displayField }
            }
          };
        } else {
          displayField.selectListUrl = selectListUrl;
          column.filterType = 'lookup';
          column.editorOptions = {
            editable: true,
            click:    this.LMLookupClickFunction,
            clickArguments: {
              column: { displayField }
            }
          };
        }

        // Needs to go onto displayField since clickArguments is really just sending the column.displayField
        displayField.allowMultiSelections = this.allowMultipleFilterTerms(displayField);

        if (displayField.type === 'NUMERIC') {
          column.filterConditions = this.numericFilterConditions;
          if (displayField.allowMultiSelections) {
            (column as any).filterMaskOptions = this.getFilterMaskOptions(displayField);
          } else {
            column.maskOptions = this.getMaskOptions(displayField);
          }
        } else {
          column.filterConditions = this.alphaFilterConditions;
        }
        return;
      }

      if (displayField.lplFieldInfo) {
        column.filterType = 'lookup';
        column.editorOptions = {
          editable: true,
          click:    this.LMFieldLookupClickFunction,
          clickArguments: {
            column: {
              displayField
            }
          }
        };

        if (displayField.type === 'NUMERIC') {
          column.filterConditions = this.numericFilterConditions;
        } else {
          column.filterConditions = this.alphaFilterConditions;
        }
        return;
      }

      if (displayField.compactFormatByForm) {
        displayField.id = column.id;
        column.filterConditions = this.codeBlockFilterConditions;
        column.filterType = 'lookup';
        column.editorOptions = {
          editable: false,
          icon: 'icon-launch',
          clearable: true,
          clear:  this.LMFilterClearClickFunction,
          clearArguments: {
            column: {
              displayField
            }
          },
          click:  this.LMFilterLookupClickFunction,
          clickArguments: {
            column: {
              displayField
            }
          }
        };
        return;
      }

      if (displayField.scannable && this.lmContextService.insideInforGo()) {
        displayField.id = column.id;
        column.filterType = 'lookup';
        column.editorOptions = {
          id: displayField.id,
          editable: true,
          icon: 'icon-barcode',
          click:    this.LMBarCodeClickFunction,
          clickArguments: {
            column: { displayField }
          }
        };
        return;
      }

      // Are multiple search terms allowed to be entered into the filter cell
      displayField.allowMultiSelections = this.allowMultipleFilterTerms(displayField);

      switch (type) {
        case AppConstants.NUMERIC:
          column.filterType = 'integer';
          column.filterConditions = this.numericFilterConditions;
          this.setFilterMaskOptions(column, displayField);
          break;

        case AppConstants.BCD:
          column.filterType = 'decimal';
          column.filterConditions = this.numericFilterConditions;
          this.setFilterMaskOptions(column, displayField);
          break;

        case AppConstants.PERCENT:
          column.filterType = 'percent';
          column.filterConditions = this.numericFilterConditions;
          this.setFilterMaskOptions(column, displayField);
          break;

        case AppConstants.BOOLEAN:
          column.filterType = 'select';
          if (!this.lmListUtilService.isAutoFilterEnabled()) {
            column.editorOptions = {
              onKeyDown: this.LMFilterDropdownKeydownFunction
            };
          };
          column.options =
            [
              {
                label: Soho.Locale.translate('Yes'),
                value: true
              },
              {
                label: Soho.Locale.translate('No'),
                value: false
              }
            ];
          break;
        case AppConstants.YYYY:
          column.filterType = 'date';
          column.dateFormat = 'yyyy';
          column.editorOptions = {
            dateFormat: 'yyyy',
            showMonthYearPicker: true,
            hideDays: true,
            incrementWithKeyboard: true,
            todayWithKeyboard: true
          };
          break;
        case AppConstants.TIMESTAMP:
          column.filterType = 'date';
          column.dateFormat = Soho.Locale.calendar().dateFormat.short + ' ' + Soho.Locale.calendar().dateFormat.timestamp;
          column.editorOptions = {
            showTime: true,
            showMonthYearPicker: true,
            dateFormat: Soho.Locale.calendar().dateFormat.short + ' ' + Soho.Locale.calendar().dateFormat.timestamp,
            incrementWithKeyboard: true,
            todayWithKeyboard: true
          };
          break;
        case AppConstants.YYYYMMDD:
          column.filterType = 'date';
          column.dateFormat = Soho.Locale.calendar().dateFormat.short;
          column.editorOptions = {
            dateFormat: Soho.Locale.calendar().dateFormat.short,
            showMonthYearPicker: true,
            incrementWithKeyboard: true,
            todayWithKeyboard: true
          };
          break;
        case AppConstants.YYYYMM:
          column.filterType = 'date';
          column.dateFormat = Soho.Locale.calendar().dateFormat.year;
          column.editorOptions = {
            dateFormat: Soho.Locale.calendar().dateFormat.year,
            showMonthYearPicker: true,
            hideDays: true,
            incrementWithKeyboard: true,
            todayWithKeyboard: true
          };
          break;
        case AppConstants.MMDD:
          column.filterType = 'date';
          column.dateFormat = Soho.Locale.calendar().dateFormat.month;
          column.editorOptions = {
            dateFormat: Soho.Locale.calendar().dateFormat.month,
            incrementWithKeyboard: true,
            todayWithKeyboard: true
          };
          break;
        case  AppConstants.TIME:
          column.filterType = 'time';
          column.editorOptions = {
            roundToInterval: false,
            timeFormat: Soho.Locale.calendar().dateFormat.timestamp
          };
          break;

        case AppConstants.ALPHALOWER:
        case AppConstants.ALPHAUPPER:
        case AppConstants.ALPHARIGHT:
        case AppConstants.TEXT:
        case AppConstants.TEXTAREA:
          column.filterType = 'text';
          let filterStr = '';
          if (listSpec.busListSpec.alphaDefaultFilter === 'startsWith') {
            filterStr = 'start-with';
          } else if (listSpec.busListSpec.alphaDefaultFilter === 'equals') {
            filterStr = 'equals';
          }
          if(filterStr !== '') {
            const filterDefault = this.alphaFilterConditions.map((filterType: string) => {
              if (filterType === filterStr) {
                return { value: filterStr, selected: true };
              }
              return filterType;
            });
            column.filterConditions = filterDefault as any;
          }
          break;
        case AppConstants.CURRENCY:
        case AppConstants.BINARY:
        default:
          column.filterType = 'text';
          break;
      }
    }
  }

  private setColumnEditor(listUrlInfo: LMListUrlInfo, listSpec: LMListSpec, column: ListGridColumn, displayField: LMListDisplayField) {
    const type = displayField.type.toUpperCase();

    if (!this.columnRequiresEditor(listSpec, displayField)) {
      return;
    }

    if (displayField.type !== AppConstants.CHECKCONTROL) {
      column.isEditable = this.LMIsEditable;
    }

    const minuteInterval = column.displayField.limitToMinuteIncrements || 1;

    if (displayField.isStaticStateField) {
      column.editor = Soho.Editors.Dropdown;
      column.options = this.lmFormatUtilService.fieldStatesToOptionsArray(displayField.states, false);
    } else if (displayField.isUserDefinedStateField) {
      column.editor = Soho.Editors.Dropdown;
      column.editorOptions = {
        editable: false,
        source:   this.LMStateFieldSource
      };
    } else {
      switch (type) {
        case AppConstants.NUMERIC:
        case AppConstants.BCD:
        case AppConstants.PERCENT:
          column.editor = Soho.Editors.Input;
          column.maskOptions = column.maskOptions ? column.maskOptions : this.getMaskOptions(displayField);

          if (type === AppConstants.PERCENT) {
            column.serialize = this.LMPercentSerializer;
          } else if (type === AppConstants.BCD) {
            column.serialize = this.LMBcdSerializer;
            column.maskOptions = this.LMMaskOptionsFunction;
          }
          break;

        case AppConstants.YYYYMM:
          column.editor = Soho.Editors.Date;
          column.serialize = this.LMPeriodSerializer;
          column.editorOptions = { incrementWithKeyboard: true, todayWithKeyboard: true };
          break;

        case AppConstants.TIME:
          column.editor = Soho.Editors.Time;
          column.serialize = this.LMTimeSerializer;
          const timeFormat = column.displayField.limitToMinuteIncrements ? Soho.Locale.calendar().timeFormat : Soho.Locale.calendar().dateFormat.timestamp;
          column.editorOptions = { showDate: false, showTime: true, minuteInterval, secondInterval: 1, timeFormat, incrementWithKeyboard: true, todayWithKeyboard: true };
          break;

        case AppConstants.TIMESTAMP:
          column.editor = Soho.Editors.Date;

          if (column.displayField.displayDateOnly) {
            column.serialize = this.LMDateSerializer;
            column.dateShowFormat = Soho.Locale.calendar().dateFormat.short;
            column.editorOptions = { incrementWithKeyboard: true, todayWithKeyboard: true };

          } else if (column.displayField.displayTimeOnly) {
            column.serialize = this.LMTimestampSerializer;
            column.dateShowFormat = Soho.Locale.calendar().dateFormat.timestamp;
            column.editorOptions = { showDate: false, showTime: true, minuteInterval, dateFormat: Soho.Locale.calendar().dateFormat.time, incrementWithKeyboard: true, todayWithKeyboard: true };

          } else if (column.displayField.displayDateAndHoursAndMinutes || column.displayField.limitToMinuteIncrements) {
            column.serialize = this.LMTimestampSerializer;
            column.dateShowFormat = Soho.Locale.calendar().dateFormat.datetime;
            column.editorOptions = { minuteInterval, showTime: true, showMonthYearPicker: true, incrementWithKeyboard: true, todayWithKeyboard: true,
              dateFormat: Soho.Locale.calendar().dateFormat.datetime };
          } else {
            column.serialize = this.LMTimestampSerializer;
            column.dateShowFormat = Soho.Locale.calendar().dateFormat.short + ' ' + Soho.Locale.calendar().dateFormat.timestamp;
            column.editorOptions = { secondInterval: 1, minuteInterval, showTime: true, showMonthYearPicker: true, incrementWithKeyboard: true, todayWithKeyboard: true,
              dateFormat: Soho.Locale.calendar().dateFormat.short + ' ' + Soho.Locale.calendar().dateFormat.timestamp };
          }
          break;

        case AppConstants.MMDD:
          column.editor = Soho.Editors.Date;
          column.serialize = this.LMAnniversarySerializer;
          column.editorOptions = { incrementWithKeyboard: true, todayWithKeyboard: true };
          break;

        case AppConstants.YYYY:
          column.editor = Soho.Editors.Date;
          column.serialize = this.LMYearSerializer;
          column.editorOptions = { showMonthYearPicker: true, hideDays: true, dateFormat: 'yyyy', incrementWithKeyboard: true, todayWithKeyboard: true };
          break;

        case AppConstants.YYYYMMDD:
          column.editor = Soho.Editors.Date;
          column.serialize = this.LMDateSerializer;
          column.editorOptions = { incrementWithKeyboard: true, todayWithKeyboard: true };
          break;

        case AppConstants.BOOLEAN:
          column.editor = Soho.Editors.Dropdown;
          column.serialize = this.LMBooleanSerializer;
          column.options =
            [
              {
                label: Soho.Locale.translate('Yes'),
                value: true
              },
              {
                label: Soho.Locale.translate('No'),
                value: false
              }
            ];
          break;

        case AppConstants.TEXT:
          if (this.hasCardView(listSpec.busListSpec.displayFields)) {
            column.editor = Soho.Editors.Textarea;
          } else {
            column.editor = Soho.Editors.Input;
          }
          break;

        case AppConstants.TEXTAREA:
          column.editor = Soho.Editors.Textarea;
          break;

        case AppConstants.CHECKCONTROL:
          if (column.displayField.validity) {
            column.isEditable = this.LMIsCheckControlEditable;
          }
          column.editor = (displayField.displayAsStar) ? Soho.Editors.Favorite : Soho.Editors.Checkbox;
          break;

        case 'CURRENCY':
        case 'ALPHAUPPER':
        case 'ALPHALOWER':
        case 'ALPHARIGHT':
        case 'BINARY':
          if (displayField.compactFormatByForm) {
            column.editorComponent = LMCodeBlockEditorComponent;
            column.serialize = this.LMCodeBlockSerializer;
            column.doNotEmptyCellWhenEditing = true;
          } else {
            column.editor = Soho.Editors.Input;
            if (displayField.size) {
              column.maxLength = displayField.size;
            }
          }
          column.uppercase = (type === 'ALPHAUPPER');
          break;

        default:
          if (displayField.compactFormatByForm) {
            column.editorComponent = LMCodeBlockEditorComponent;
            column.serialize = this.LMCodeBlockSerializer;
            column.doNotEmptyCellWhenEditing = true;
          } else {
            column.editor = Soho.Editors.Input;
            if (displayField.size) {
              column.maxLength = displayField.size;
            }
          }
          break;
      }

      if (displayField.selectable && listSpec.listType !== 'select') {
        const selectListUrl = this.lmUrlUtilService.buildSelectListUrl(listUrlInfo, displayField.selectListInfo);
        if (!this.lmContextService.useNewSpecUrls()) {
          this.lmUrlInfoBuilderService.compareSpecUrls(listUrlInfo, displayField.bindId + '.selectListUrl', (displayField as any).selectListUrl, selectListUrl);
        }

        // todo spec-url-refactor, have to piggy back on displayfield for selectListUrl for some reason.
        //   can't just put selectListUrl into clickArguments, seems to get overridden.
        displayField.selectListUrl = selectListUrl;
        column.editor = Soho.Editors.Lookup;
        column.editorOptions = {
          editable: true,
          click:    this.LMLookupClickFunction,
          clickArguments: {
            column: { displayField }
          }
        };
      }

      if (displayField.lplFieldInfo) {
        column.editor = Soho.Editors.Lookup;
        column.editorOptions = {
          editable: true,
          click:    this.LMFieldLookupClickFunction,
          clickArguments: {
            column: {
              displayField
            }
          }
        };
      }

      if (displayField.scannable && this.lmContextService.insideInforGo()) {
        displayField.id = column.id;
        column.editor = Soho.Editors.Lookup;
        column.editorOptions = {
          id: displayField.id,
          editable: true,
          icon: 'icon-barcode',
          click:    this.LMBarCodeClickFunction,
          clickArguments: {
            column: { displayField }
          }
        };
      }
    }
  }

  // DPB for easier testing this should return the alignment
  private setColumnAlignment(column: ListGridColumn, displayField: LMListDisplayField) {

    // does the display field have an override
    if (displayField.align) {
      column.align = displayField.align;
      return;
    }

    // special cases
    if (displayField.type === AppConstants.NUMERIC && (displayField.isSymbolicKey || displayField.isKey)) {
      column.align = AppConstants.ALIGN_LEFT;
      return;
    }

    if (displayField.displayAsProgressBar) {
      column.align = AppConstants.ALIGN_LEFT;
      return;
    }

    if (displayField.displayAsRating) {
      column.align = AppConstants.ALIGN_LEFT;
      return;
    }

    if (displayField.isStaticStateField || displayField.isUserDefinedStateField) {
      column.align = AppConstants.ALIGN_CENTER;
      return;
    }

    // default
    switch (displayField.type) {
      case AppConstants.BCD:
      case AppConstants.BOOLEAN:
      case AppConstants.CURRENCY:
      case AppConstants.PERCENT:
      case AppConstants.ALPHARIGHT:
      case AppConstants.NUMERIC:
        column.align = AppConstants.ALIGN_RIGHT;
        column.headerAlign = 'right';
        break;
      case AppConstants.CHECKCONTROL:
      case AppConstants.FORMBUTTON:
      case AppConstants.FORMREFERENCE:
      case AppConstants.LISTREFERENCE:
      case AppConstants.NAVIGATION:
      case AppConstants.SUBFORM:
        column.align = AppConstants.ALIGN_CENTER;
        break;
      default:
        column.align = AppConstants.ALIGN_LEFT;
        break;
    }
  }

  private isSortable(listSpec: LMListSpec, displayField: LMListDisplayField): boolean {
    if (listSpec.busListSpec.implementsTreeView) {
      return false;
    }

    // when the list has a card view with sorts and has called out action is sort
    // then column header sorting is not enabled for any column
    if (this.lmListSpecUtilService.isGridWithCardViewSorts(listSpec) && this.lmListSpecUtilService.hasSortCalledOut(listSpec)) {
      return false;
    }

    // when the list has a card view with sorts and has not called out action is sort
    // then column header sorting is enabled for the card view
    if (this.lmListSpecUtilService.isGridWithCardViewSorts(listSpec)
        && displayField.cardViewSortOrders
        && !this.lmListSpecUtilService.hasSortCalledOut(listSpec)) {
      return true;
    }

    if (displayField.sortOrderName !== undefined && !displayField.isTreeColumn) {
      return true;
    }

    if (this.lmListSpecUtilService.isColumnarView(listSpec)) {
      return true;
    }

    return false;
  }

  private columnRequiresEditor(listSpec: LMListSpec, displayField: LMListDisplayField): boolean {
    const type = displayField.type.toUpperCase();

    // helper lists are not editable
    if (listSpec.listType === 'helper') {
      return false;
    }

    // dashboard lists are not editable, but a checkcontrol requires an editor to handle the click event
    if (listSpec.listType === 'dashboard' && type !== AppConstants.CHECKCONTROL) {
      return false;
    }

    // these columns never require an editor
    if (displayField.isTreeColumn || type === 'SUBFORM' || type === 'FORMBUTTON' || displayField.isNotMutable || displayField.uiProtected) {
      return false;
    }

    // when list implements inlineCreate, require an editor on the editable fields
    // when column allowsUpdate it requires an editor
    return (listSpec.busListSpec.implementsInlineCreate || displayField.allowUpdate);
  }

  private setFilterMaskOptions(column: ListGridColumn, displayField: LMListDisplayField) {
    if (displayField.allowMultiSelections) {
      (column as any).filterMaskOptions = this.getFilterMaskOptions(displayField);
      column.filterType = 'text';
    } else if (displayField.type === AppConstants.BCD) {
      (column as any).filterMaskOptions = this.getMaskOptions(displayField);
    } else {
      column.maskOptions = this.getMaskOptions(displayField);
    }
  }

  private getDecimalLimit(allowDecimal: boolean, percentAdjustment: number, displayField: LMListDisplayField): number {

    if (allowDecimal) {
      if (displayField.numberOfDecimals  || displayField.numberOfDecimals === 0) {
        // if the formField has the display # decimals syntax it will return this first.
        return displayField.numberOfDecimals;
      }

      return displayField.decSize - percentAdjustment;
    }

    return 0;
  }

  private getMaskOptions(displayField: LMListDisplayField): SohoMaskOptions {
    const type = displayField.type.toUpperCase();

    const percentAdjustment: number = (type === AppConstants.PERCENT) ? 2 : 0;
    const allowDecimal: boolean = (displayField.decSize !== undefined && displayField.decSize - percentAdjustment > 0);
    const decimalLimit: number = this.getDecimalLimit(allowDecimal, percentAdjustment, displayField);
    const integerLimit: number = (allowDecimal) ? displayField.size - decimalLimit : displayField.size;

    const maskOptions: SohoMaskOptions = {
      process:        'number',
      patternOptions: {
        allowDecimal,
        allowNegative: !displayField.positiveOnly,
        integerLimit,
        decimalLimit,
        allowLeadingZeros: true,
        allowThousandsSeparator: false,
        symbols:       {
          decimal:   Soho.Locale.numbers().decimal,
          negative:  Soho.Locale.numbers().minusSign,
          thousands: Soho.Locale.numbers().group
        }
      }
    };

    return maskOptions;
  }

  private getFilterMaskOptions(displayField: LMListDisplayField): SohoMaskOptions {
    if (!this.lmContextService.useMultiSelectKeyField()) {
      return;
    }

    if (displayField.type !== AppConstants.BCD
      && displayField.type !== AppConstants.PERCENT
      && displayField.type !== AppConstants.NUMERIC) {
      return;
    }

    const percentAdjustment: number = (displayField.type === AppConstants.PERCENT) ? 2 : 0;
    const allowDecimal: boolean = (displayField.decSize !== undefined && displayField.decSize - percentAdjustment > 0);
    const decimalLimit: number = (allowDecimal) ? displayField.decSize - percentAdjustment : 0;
    const integerLimit: number = (allowDecimal) ? displayField.size - decimalLimit : displayField.size;
    const decimal = allowDecimal ? Soho.Locale.numbers().decimal : '';
    const negative = !!displayField.positiveOnly ? '' : Soho.Locale.numbers().minusSign;
    const thousands = decimal ? Soho.Locale.numbers().group : '';
    const anyDigit = '\\d';
    const space = ' ';

    const maskOptions: SohoMaskOptions = {
      // eslint-disable-next-line prefer-arrow/prefer-arrow-functions,no-unused-vars
      pattern: function customMask(rawValue, options) {
        const match = new RegExp(`[${negative}${decimal}${thousands}${space}${AppConstants.MULTI_SEARCH_TERM_DELIMITER}${anyDigit}]`);
        return rawValue.split('').map(() => match);
      }
    };

    return maskOptions;
  }

  private allowMultipleFilterTerms(displayField: LMListDisplayField): boolean {
    if (!this.lmContextService.useMultiSelectKeyField()) {
      return false;
    }

    return this.lmFieldUtilService.typeAllowsMultipleSearchTerms(displayField.type);
  }

  private isTextAreaOrText(displayField: LMListDisplayField): boolean {
    return (displayField.type === AppConstants.TEXTAREA || displayField.type === AppConstants.TEXT);
  }

  private hasCardView(displayFields: LMListDisplayField[]): boolean {
    return displayFields.some(displayField => displayField.type.toUpperCase() === AppConstants.CARDVIEW);
  }

  private hasCodeBlock(displayFields: LMListDisplayField[]): boolean {
    return displayFields.some(displayField => displayField.compactFormatByForm);
  }

  private setColumnSpan(listSpec: LMListSpec, column: ListGridColumn) {
    if (column.isAggField || this.hasTotalDefined(listSpec.busListSpec.displayFields, listSpec)) {
      column.colspan = this.LMColSpanFormatter;
    }
  }

  private hasTotalDefined(displayFields: LMListDisplayField[], listSpec: LMListSpec): boolean {
    if (listSpec.busListSpec.suppressSubTotals) {
      return true;
    }

    return displayFields.some(df => df.isTotal || df.isRunningTotal || df.isPercentOfTotal);
  }

  private setColumnWidthWithShowUpTo(column: ListGridColumn, displayField: LMListDisplayField, listSpec: LMListSpec) {
    const columnPadding = 10;
    const pixelsPerChar = this.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout ? 5 : 6;
    const columnSizing = (listSpec.busListSpec.horizontalScrolling) ? 'both' : 'data';

    let prefWidth = 10;
    let columnHeaderWidth = 0;
    let totalFieldSize = displayField.showChars;

    if (column.name) {
      columnHeaderWidth = (pixelsPerChar * column.name.length) + columnPadding;
    }

    switch (displayField.type) {
      case 'NUMERIC':
      case 'DOUBLE':
      case 'DECVAR':
      case 'YYYY':
        prefWidth += pixelsPerChar * totalFieldSize;
        break;

      case 'PERCENT':
        prefWidth += (pixelsPerChar * totalFieldSize) + (pixelsPerChar * '%'.length);
        break;

      case 'YYYYMMDD':
        prefWidth += (pixelsPerChar * totalFieldSize) + (pixelsPerChar * '//'.length);
        break;

      case 'YYYYMM':
        prefWidth += (pixelsPerChar * totalFieldSize) + (pixelsPerChar * '/'.length);
        break;

      case 'TIME':
        prefWidth += (pixelsPerChar * totalFieldSize) + (pixelsPerChar * '::AM'.length);
        break;

      case 'TIMESTAMP':
        if (displayField.displayHoursAndMinutesOnly) {
          prefWidth += (pixelsPerChar * totalFieldSize) + (pixelsPerChar * ':'.length);
        } else if (displayField.displayDateOnly) {
          prefWidth += (pixelsPerChar * totalFieldSize) + (pixelsPerChar * '//'.length);
        } else if (displayField.displayTimeOnly) {
          prefWidth += (pixelsPerChar * totalFieldSize) + (pixelsPerChar * '::AM'.length);
        } else {
          prefWidth += (pixelsPerChar * totalFieldSize) + (pixelsPerChar * '::AM'.length);
        }
        break;

      case 'BOOLEAN':
        let optionValueLength = 0;
        if (column.options) {
          for (const option of column.options) {
            optionValueLength = (option.label !== undefined && option.label.length > optionValueLength)
              ? option.label.length : optionValueLength;
          }
        }

        totalFieldSize = (optionValueLength === 0) ? totalFieldSize : optionValueLength;
        prefWidth += (pixelsPerChar * totalFieldSize) + 20;
        break;

      case 'TEXT':
      case 'TEXTAREA':
        totalFieldSize = 50;
        prefWidth += pixelsPerChar * totalFieldSize;
        break;

      default: // alpha and others
        prefWidth += pixelsPerChar * totalFieldSize;
        break;
    }

    prefWidth = Math.min(200, prefWidth);
    column.width = (columnSizing === 'both') ? Math.max(prefWidth, columnHeaderWidth) : prefWidth;
  }

  // landmark formatters
  LMButtonFormatter = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string =>
    lmButtonFormatter(this.lmFormatterService, this.lmFormatUtilService, row, cell, value, columnDef, dataView, dataGrid);

  LMCardViewFormatter = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string =>
    lmCardViewFormatter.format(this.lmFormatterService, this.lmListCardService, this.lmUrlUtilService, row, cell, value, columnDef, dataView, dataGrid, 'lm-datagrid-card-cell-container');

  LMCodeBlockFormatter = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string =>
    lmCodeBlockFormatter(this.lmFormatterService, this.lmFormatUtilService, this.lmCommonUtilService, row, cell, value, columnDef, dataView, dataGrid);

  LMFavoriteFormatter = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string =>
    lmFavoriteFormatter(this.lmFormatterService, row, cell, value, columnDef, dataView, dataGrid);

  LMProgressFormatter = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string =>
    lmProgressFormatter(this.lmFormatterService, this.lmFormatUtilService, this.lmCommonUtilService, row, cell, value, columnDef, dataView, dataGrid);

  LMTreeFormatter = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string =>
    lmTreeFormatter(this.lmFormatterService, row, cell, value, columnDef, dataView, dataGrid);

  LMCheckBoxFormatter = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string =>
    lmCheckBoxFormatter(this.lmFormatterService, row, cell, value, columnDef, dataView, dataGrid);

  LMColSpanFormatter = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): number =>
    colSpan(this.lmFormatterService, this.lmConditionUtilService, row, cell, value, columnDef, dataView, dataGrid);

  LMListCellTooltip = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string =>
    lmListCellTooltip(this.lmFormatterService, this.lmFormatUtilService, this.lmProcessIndicatorService, this.lmProcessIndicatorTooltipPipe,
      this.lmListCardService, row, cell, value, columnDef, dataView, dataGrid);

  LMFieldFormatter = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string =>
    lmFieldFormatter(this.lmFormatterService, this.lmFormatUtilService, row, cell, value, columnDef, dataView, dataGrid);

  LMRatingFormatter = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string =>
    lmRatingFormatter(this.lmFormatterService, this.lmFormatUtilService, row, cell, value, columnDef, dataView, dataGrid)

  LMProcessIndicatorFormatter = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView): string =>
    lmProcessIndicatorFormatter.format(this.lmProcessIndicatorService, row, cell, value, columnDef, dataView);

  // landmark editors
  LMIsCheckControlEditable: LMListGridColumnIsEditable = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: any, dataGrid: SohoDataGridStatic): boolean =>
    isCheckControlEditable(this.lmEditorService, row, cell, value, columnDef, dataView, dataGrid);

  LMIsEditable = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView): boolean =>
    isEditable(this.lmFormatterService, this.lmEditorService, this.lmActionService, columnDef, dataView);

  LMStateFieldSource = (response: any, searchTerm: any, sourceArguments: any) =>
    lmStateFieldSource(this.lmEditorService, this.lmFieldUtilService, this.lmFormatUtilService, this.lmContextService, response, searchTerm, sourceArguments);

  LMBarCodeClickFunction = (event: any, lookup: SohoLookupStatic, clickArguments: any) =>
    lmBarCodeClickFunction(this.lmFormatUtilService, event, lookup, clickArguments);

  LMLookupClickFunction = (event: any, lookup: SohoLookupStatic, clickArguments: any) =>
    lmLookupClickFunction(this.lmFormatUtilService, event, lookup, clickArguments);

  LMFieldLookupClickFunction = (event: any, lookup: SohoLookupStatic, clickArguments: any) =>
    lmFieldLookupClickFunction(event, lookup, clickArguments);

  LMMaskOptionsFunction = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView): SohoMaskOptions =>
    lmMaskOptionsFunction(this.lmFormatterService, row, cell, value, columnDef, dataView);

  // landmark filters
  LMFilterLookupClickFunction = (event: any, filterLookup: SohoLookupStatic, clickArguments: any) =>
    lmFilterLookupClickFunction(event, filterLookup, clickArguments);

  LMFilterClearClickFunction = (event: any, filterLookup: SohoLookupStatic, clearArguments: any) =>
    lmFilterClearClickFunction(event, filterLookup, clearArguments);

  LMFilterDropdownKeydownFunction = (event: JQuery.Event) =>
    lmFilterDropdownKeydownFunction(event);

  // landmark serializers
  LMBcdSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmBcdSerializer(this.lmFormatterService, this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView);

  LMBooleanSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): boolean =>
    lmBooleanSerializer(this.lmFormatterService, this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView);

  LMCodeBlockSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmCodeBlockSerializer(this.lmFormatUtilService, this.lmCompoundFieldService, value, oldValue, columnDef, row, cell, dataView);

  LMPercentSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmPercentSerializer(this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView);

  // do we really need all these for the data nad time fields?  also lets give them better names
  LMYearSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmDateTimeSerializer(this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView, AppConstants.YYYY);

  LMPeriodSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmDateTimeSerializer(this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView, AppConstants.YYYYMM);

  LMAnniversarySerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmDateTimeSerializer(this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView, AppConstants.MMDD);

  LMDateSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmDateTimeSerializer(this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView, AppConstants.YYYYMMDD);

  LMTimestampSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmDateTimeSerializer(this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView, AppConstants.TIMESTAMP);

  LMTimeSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmDateTimeSerializer(this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView, AppConstants.TIME);
}
